import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { QRCodeCanvas } from "qrcode.react";
import { Download, Share2, Wallet, ExternalLink } from "lucide-react";
import html2canvas from "html2canvas";
import { BACKEND_URL } from "@/lib/api";

export default function AssocCard() {
  const { user } = useAuth();
  const [assoc, setAssoc] = useState(null);
  const cardRef = useRef(null);

  useEffect(() => {
    if (user?.associate_id) api.get(`/associates/${user.associate_id}`).then(({ data }) => setAssoc(data));
  }, [user]);

  const download = async () => {
    if (!cardRef.current) return;
    const canvas = await html2canvas(cardRef.current, { backgroundColor: null, scale: 2 });
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a"); a.href = url; a.download = `targeta-soci-${assoc.member_number}.png`; a.click();
  };
  const share = async () => {
    if (!navigator.share) { download(); return; }
    if (!cardRef.current) return;
    const canvas = await html2canvas(cardRef.current, { backgroundColor: null, scale: 2 });
    canvas.toBlob(async (blob) => {
      const file = new File([blob], `targeta-${assoc.member_number}.png`, { type: "image/png" });
      try { await navigator.share({ files: [file], title: "Targeta Shopping Salou" }); } catch {}
    });
  };

  if (!assoc) return <div className="text-muted-foreground">Carregant…</div>;
  const publicWalletUrl = `${window.location.origin}/wallet/${assoc.member_number}`;
  const qrPayload = publicWalletUrl;

  return (
    <div className="space-y-8 max-w-2xl mx-auto" data-testid="assoc-card-page">
      <div>
        <div className="overline text-accent">Identificació</div>
        <h1 className="mt-2 text-3xl font-heading font-bold tracking-tight">Targeta digital de soci</h1>
      </div>

      <div ref={cardRef} className="rounded-3xl overflow-hidden shadow-2xl shadow-primary/20 relative">
        <div className="bg-primary text-primary-foreground p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-accent font-semibold">Shopping Salou</div>
              <div className="text-xs opacity-75 mt-1">Associació de Comerciants</div>
            </div>
            <img src="/logo.jpg" alt="Shopping Salou" className="w-14 h-14 rounded-2xl bg-white/95 p-1 object-contain" />
          </div>
          <div className="text-2xl font-heading font-bold leading-tight">{assoc.commercial_name}</div>
          <div className="text-sm opacity-80 mt-1">{assoc.legal_name || "—"}</div>
          <div className="mt-6 flex items-end justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Número de soci</div>
              <div className="font-mono text-xl font-bold mt-0.5">{assoc.member_number}</div>
              <div className="text-[10px] opacity-70 mt-2">Alta · {new Date(assoc.created_at).toLocaleDateString()}</div>
            </div>
            <div className="bg-white p-2 rounded-xl">
              <QRCodeCanvas value={qrPayload} size={110} bgColor="#ffffff" fgColor="#CF0283" level="M" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button onClick={download} size="lg" className="rounded-full" data-testid="card-download-btn"><Download className="w-4 h-4 mr-2" /> Descarregar</Button>
        <Button onClick={share} variant="outline" size="lg" className="rounded-full" data-testid="card-share-btn"><Share2 className="w-4 h-4 mr-2" /> Compartir</Button>
        <Button asChild variant="outline" size="lg" className="rounded-full" data-testid="card-wallet-btn">
          <a href={publicWalletUrl} target="_blank" rel="noreferrer"><Wallet className="w-4 h-4 mr-2" /> Afegir al mòbil</a>
        </Button>
        <Button asChild variant="ghost" size="lg" className="rounded-full" data-testid="card-vcard-btn">
          <a href={`${BACKEND_URL}/api/wallet/${assoc.member_number}/vcard`}><ExternalLink className="w-4 h-4 mr-2" /> vCard</a>
        </Button>
      </div>

      <div className="text-xs text-muted-foreground bg-secondary/60 rounded-xl px-4 py-3">
        <strong>Enllaç públic:</strong> <a href={publicWalletUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">{publicWalletUrl}</a>
        <br />Aquest enllaç s'obre a qualsevol mòbil i es pot "afegir a pantalla d'inici" per convertir la targeta en app.
      </div>

      <Card className="rounded-2xl border-2">
        <CardContent className="p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Dades del comerç</div>
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div><dt className="text-muted-foreground">CIF</dt><dd className="font-mono">{assoc.cif || "—"}</dd></div>
            <div><dt className="text-muted-foreground">Categoria</dt><dd>{assoc.category}</dd></div>
            <div className="col-span-2"><dt className="text-muted-foreground">Adreça</dt><dd>{assoc.address || "—"}</dd></div>
            <div><dt className="text-muted-foreground">Telèfon</dt><dd>{assoc.phone}</dd></div>
            <div><dt className="text-muted-foreground">Email</dt><dd>{assoc.email}</dd></div>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}
